import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { StickyNotificationHeader } from '../components/NotificationHeader/StickyNotificationHeader';

/**
 * Information about a registered header component
 */
export interface HeaderInfo {
  /** Unique identifier for the header */
  id: string;
  /** Display text for the header */
  label: string;
  /** Vertical position of the header in the viewport */
  verticalPosition: number;
  /** Type of header */
  type: 'section' | 'group';
  /** Callback function to be called when the showLess button is clicked */
  showLessCallback?: () => void;
}

/**
 * Visibility state for a header component
 */
interface HeaderVisibilityState {
  /** Whether the header has scrolled above the viewport */
  isAboveViewport: boolean;
}

/**
 * Context type for managing sticky headers
 */
interface StickyHeaderContextType {
  /** Register a new header with the context */
  registerHeader: (header: HeaderInfo) => void;
  /** Remove a header from the context */
  unregisterHeader: (headerId: string) => void;
  /** Update the visibility state of a header */
  updateHeaderVisibility: (headerId: string, isAboveViewport: boolean) => void;
  /** Array of headers that should be displayed as sticky */
  visibleHeaders: HeaderInfo[];
  /** Map of all registered headers */
  registeredHeaders: Map<string, HeaderInfo>;
}

const StickyHeaderContext = createContext<StickyHeaderContextType | null>(null);

/**
 * StickyHeaderProvider Component
 * Provides context for managing multiple sticky headers in a sequential manner.
 * Only displays the most recently scrolled header as sticky, creating a smooth
 * transition between sections as the user scrolls.
 *
 * Key Features:
 * - Manages registration and unregistration of headers
 * - Tracks visibility state of all headers
 * - Determines which header should be sticky based on scroll position
 * - Orders headers by their vertical position in the viewport
 * - Prevents infinite re-renders with memoized functions and values
 *
 * @param props - Component props
 * @returns Provider component wrapping children with sticky header context
 */
export const StickyHeaderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [registeredHeaders, setRegisteredHeaders] = useState<
    Map<string, HeaderInfo>
  >(new Map());
  const [headerVisibility, setHeaderVisibility] = useState<
    Map<string, HeaderVisibilityState>
  >(new Map());

  /**
   * Calculate which headers should be sticky - show only the most recent one
   * Headers are ordered by their vertical position in the viewport
   */
  const visibleHeaders = useMemo(() => {
    // Get all headers that are above viewport
    const aboveViewportSectionHeaders = Array.from(headerVisibility.entries())
      .filter(([, state]) => state.isAboveViewport)
      .map(([id]) => registeredHeaders.get(id))
      .filter(
        (header): header is HeaderInfo =>
          header !== undefined && header.type === 'section'
      )
      .sort((a, b) => b.verticalPosition - a.verticalPosition); // Higher position = closer to top

    const aboveViewportGroupHeaders = Array.from(headerVisibility.entries())
      .filter(([, state]) => state.isAboveViewport)
      .map(([id]) => registeredHeaders.get(id))
      .filter(
        (header): header is HeaderInfo =>
          header !== undefined && header.type === 'group'
      )
      .sort((a, b) => b.verticalPosition - a.verticalPosition); // Higher position = closer to top

    // Only show the last (most recent) header that went above viewport
    const lastSectionHeader =
      aboveViewportSectionHeaders.length > 0
        ? [aboveViewportSectionHeaders[0]]
        : [];
    const lastGroupHeader =
      aboveViewportGroupHeaders.length > 0
        ? [aboveViewportGroupHeaders[0]]
        : [];

    return [...lastSectionHeader, ...lastGroupHeader];
  }, [headerVisibility, registeredHeaders]);

  // Stabilize functions with useCallback to prevent infinite re-renders
  const registerHeader = useCallback((header: HeaderInfo) => {
    setRegisteredHeaders((prev) => new Map(prev.set(header.id, header)));
    setHeaderVisibility(
      (prev) => new Map(prev.set(header.id, { isAboveViewport: false }))
    );
  }, []);

  const unregisterHeader = useCallback((headerId: string) => {
    setRegisteredHeaders((prev) => {
      const next = new Map(prev);
      next.delete(headerId);
      return next;
    });
    setHeaderVisibility((prev) => {
      const next = new Map(prev);
      next.delete(headerId);
      return next;
    });
  }, []);

  const updateHeaderVisibility = useCallback(
    (headerId: string, isAboveViewport: boolean) => {
      setHeaderVisibility((prev) => {
        const current = prev.get(headerId);
        if (!current) return prev;
        const next = new Map(prev);
        next.set(headerId, { ...current, isAboveViewport });
        return next;
      });
    },
    []
  );

  const contextValue = useMemo(
    () => ({
      registerHeader,
      unregisterHeader,
      updateHeaderVisibility,
      visibleHeaders,
      registeredHeaders,
    }),
    [
      registerHeader,
      unregisterHeader,
      updateHeaderVisibility,
      visibleHeaders,
      registeredHeaders,
    ]
  );

  return (
    <StickyHeaderContext.Provider value={contextValue}>
      <StickyNotificationHeader>{children}</StickyNotificationHeader>
    </StickyHeaderContext.Provider>
  );
};

/**
 * useStickyHeaders Hook
 *
 * Custom hook for accessing sticky header context functionality.
 * Must be used within a StickyHeaderProvider.
 *
 * @returns Context object with header management functions and state
 * @throws Error if used outside of StickyHeaderProvider
 */
export const useStickyHeaders = (): StickyHeaderContextType => {
  const context = useContext(StickyHeaderContext);
  if (!context) {
    throw new Error(
      'useStickyHeaders must be used within StickyHeaderProvider'
    );
  }
  return context;
};
